/**
 * Build a `mailto:` URL that actually arrives with its body intact.
 *
 * The naive version — `mailto:x?subject=${encodeURIComponent(s)}&body=${encodeURIComponent(b)}`
 * — opens the client with the recipient and subject filled in and the body
 * silently empty. Two reasons, and the first is the one that bites:
 *
 * ## 1. Line breaks must be CRLF
 *
 * `encodeURIComponent('\n')` produces `%0A`. RFC 6068 §5 requires a line break
 * in a mailto body to be `%0D%0A`, and several clients — Outlook on Windows
 * most reliably — discard the entire body rather than the offending character
 * when they meet a bare `%0A`. Our templates are written with `\n`, so every
 * one of them hit this.
 *
 * ## 2. `encodeURIComponent` is not RFC 3986
 *
 * It leaves `!`, `'`, `(`, `)` and `*` unescaped. They are legal in a URI but
 * not in the *query* production a mailto handler parses, and Windows in
 * particular truncates the parameter at the first unescaped `'` — which every
 * English template has ("the lead's first contact").
 *
 * ## 3. Some clients simply ignore the body
 *
 * Gmail on iOS is the known one: whether it is reached through the app or the
 * installed web app, its `mailto:` handler reads `to` and `subject` and drops
 * `body` on the floor. Nothing in the URL fixes that — it is not an encoding
 * problem, the parameter is discarded. `gmailComposeUrl` exists because of it.
 *
 * ## 4. Length
 *
 * The practical ceiling is around 2,000 characters for the whole URL on
 * Windows; longer and the shell truncates without warning. `mailtoUrl` reports
 * the length so the caller can say so out loud instead of shipping a half
 * email. The current templates come in at ~1,700 at worst, so this is a guard
 * rather than a live problem — but a template is product copy and copy grows.
 */

/** Practical ceiling for the whole URL. Beyond this, Windows truncates. */
export const MAILTO_SAFE_LENGTH = 1900

/**
 * Percent-encode per RFC 3986: everything except the unreserved set
 * `A-Z a-z 0-9 - _ . ~`.
 */
function encodeStrict(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  )
}

/** Collapse a subject to one line — a newline in one makes clients reject the URL. */
function oneLine(text: string): string {
  return text.replace(/[\r\n]+/g, ' ').trim()
}

/** Normalise to bare LF, which is what an HTTPS query string wants. */
function toLf(text: string): string {
  return text.replace(/\r\n|\r/g, '\n')
}

/** Normalise every line-ending convention to the CRLF a mailto body needs. */
function toCrlf(text: string): string {
  return text.replace(/\r\n|\r|\n/g, '\r\n')
}

export interface MailtoResult {
  url: string
  length: number
  /** True when the URL is long enough that a client may truncate it. */
  tooLong: boolean
}

export function mailtoUrl(recipient: string, subject: string, body: string): MailtoResult {
  const params: string[] = []
  // A subject is a single line by definition; a stray newline in one produces a
  // header-injection-shaped URL that clients reject outright.
  if (subject) params.push(`subject=${encodeStrict(subject.replace(/[\r\n]+/g, ' ').trim())}`)
  if (body) params.push(`body=${encodeStrict(toCrlf(body))}`)

  const url =
    `mailto:${encodeStrict(recipient.trim())}` + (params.length ? `?${params.join('&')}` : '')

  return { url, length: url.length, tooLong: url.length > MAILTO_SAFE_LENGTH }
}


/**
 * Gmail's web compose URL.
 *
 * The escape hatch from `mailto:` entirely. It is an ordinary HTTPS link, so
 * every browser handles it the same way, and Gmail's own compose window reads
 * the body reliably — including on an iPad, where the `mailto:` route loses it.
 *
 * `view=cm` is compose, `fs=1` makes it a full window rather than the little
 * corner popup. Line breaks are plain LF here: this is a normal query string,
 * not the mailto production, and Gmail renders `%0A` correctly.
 */
export function gmailComposeUrl(recipient: string, subject: string, body: string): string {
  const p = new URLSearchParams({ view: 'cm', fs: '1' })
  if (recipient) p.set('to', recipient.trim())
  if (subject) p.set('su', oneLine(subject))
  if (body) p.set('body', toLf(body))
  return `https://mail.google.com/mail/?${p.toString()}`
}

/** Outlook on the web, same idea. */
export function outlookComposeUrl(recipient: string, subject: string, body: string): string {
  const p = new URLSearchParams()
  if (recipient) p.set('to', recipient.trim())
  if (subject) p.set('subject', oneLine(subject))
  if (body) p.set('body', toLf(body))
  return `https://outlook.office.com/mail/deeplink/compose?${p.toString()}`
}

/** How a rep prefers their drafts to open. Remembered per browser. */
export type ComposeTarget = 'gmail' | 'outlook' | 'mailto'

export const COMPOSE_TARGETS: { key: ComposeTarget; label: string; note: string }[] = [
  { key: 'gmail', label: 'Gmail', note: 'opens Gmail on the web \u2014 works on any device' },
  { key: 'outlook', label: 'Outlook web', note: 'opens outlook.office.com' },
  {
    key: 'mailto',
    label: 'Default mail app',
    note: 'hands over to the OS \u2014 Gmail on iOS drops the body',
  },
]


export function composeUrl(
  target: ComposeTarget,
  recipient: string,
  subject: string,
  body: string,
): string {
  if (target === 'gmail') return gmailComposeUrl(recipient, subject, body)
  if (target === 'outlook') return outlookComposeUrl(recipient, subject, body)
  return mailtoUrl(recipient, subject, body).url
}
